/**
 * Panel Admin · Migración de datos desde herramientas legacy a SOFI 2.0.
 * Ruta: /app/imports_admin (registrar en ModuleRouter + rbac.py)
 *
 * Lista los importadores registrados (Creador de Itinerarios, Pipeline,
 * Asistente de Reservas, B2B, B2C). Para cada uno:
 *   - Muestra si está configurado (env vars presentes).
 *   - Botón "Previsualizar" (dry-run) → muestra qué haría, no toca BD.
 *   - Botón "Aplicar" (real) → escribe en BD idempotente.
 *   - Muestra el último run con timestamp, stats y errores.
 */
import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, Play, Eye, RefreshCw, CheckCircle2, XCircle, AlertTriangle,
  Database, Clock, Wrench,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function ImportsAdminPage() {
  const qc = useQueryClient();
  const [previewFor, setPreviewFor] = useState(null); // {tool_slug, result}
  const [applyingFor, setApplyingFor] = useState(null);
  const [loadingSlug, setLoadingSlug] = useState(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-imports"],
    queryFn: async () => (await api.get("/admin/imports")).data,
  });

  const items = data?.items || [];

  const previewMut = useMutation({
    mutationFn: async (slug) => {
      setLoadingSlug(slug);
      const res = await api.post(`/admin/imports/${slug}/preview`);
      return { slug, result: res.data };
    },
    onSuccess: ({ slug, result }) => {
      setPreviewFor({ tool_slug: slug, result });
      qc.invalidateQueries({ queryKey: ["admin-imports"] });
    },
    onError: (e) => {
      toast.error("Error en preview: " + (e?.response?.data?.detail || e.message));
    },
    onSettled: () => setLoadingSlug(null),
  });

  const applyMut = useMutation({
    mutationFn: async (slug) => {
      setLoadingSlug(slug);
      const res = await api.post(`/admin/imports/${slug}/apply`);
      return { slug, result: res.data };
    },
    onSuccess: ({ slug, result }) => {
      if (result.ok) {
        toast.success(`Importación ${slug} aplicada · run ${result.run_id?.slice(0, 8)}`);
      } else {
        toast.error(`Fallo aplicando: ${result.error}`);
      }
      qc.invalidateQueries({ queryKey: ["admin-imports"] });
      setApplyingFor(null);
      setPreviewFor(null);
    },
    onError: (e) => {
      toast.error("Error aplicando: " + (e?.response?.data?.detail || e.message));
    },
    onSettled: () => setLoadingSlug(null),
  });

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4" data-testid="imports-admin-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Database className="h-6 w-6 text-primary" />
            Migración de datos legacy → SOFI 2.0
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Importa datos desde las herramientas antiguas (Pipeline, Creador de
            Itinerarios, Reservas, B2B, B2C) hacia SOFI 2.0 antes del apagado
            definitivo. <b>Idempotente</b>: puedes correr <i>Previsualizar</i>
            cuantas veces quieras sin efecto en la BD.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => refetch()} data-testid="imports-refresh">
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />Actualizar
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No hay importadores registrados todavía.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((it) => (
            <ImporterCard
              key={it.tool_slug}
              item={it}
              loading={loadingSlug === it.tool_slug}
              onPreview={() => previewMut.mutate(it.tool_slug)}
              onApply={() => setApplyingFor(it)}
            />
          ))}
        </div>
      )}

      {/* ─── Correcciones de datos (fixes ad-hoc con un click) ─── */}
      <DataFixesSection />

      {/* Diálogo de resultado del preview */}
      <AlertDialog
        open={!!previewFor}
        onOpenChange={(v) => !v && setPreviewFor(null)}
      >
        <AlertDialogContent className="max-w-2xl" data-testid="import-preview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Previsualización · {previewFor?.tool_slug}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {previewFor?.result?.ok ? (
                  <>
                    <p className="text-muted-foreground">
                      El importador leyó correctamente el export.
                      <b> No se ha escrito nada en la BD</b>. Revisa los conteos
                      antes de aplicar.
                    </p>
                    <div className="rounded border bg-slate-50 p-3 font-mono text-xs">
                      <div><b>Elapsed:</b> {previewFor.result.elapsed_ms} ms</div>
                      <div><b>Source counts:</b></div>
                      <pre className="mt-1 whitespace-pre-wrap">
                        {JSON.stringify(previewFor.result.source_counts, null, 2)}
                      </pre>
                      <div className="mt-2"><b>Stats por entidad:</b></div>
                      <pre className="mt-1 whitespace-pre-wrap">
                        {JSON.stringify(previewFor.result.stats, null, 2)}
                      </pre>
                    </div>
                  </>
                ) : (
                  <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                    <b>Error:</b> {previewFor?.result?.error}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cerrar</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmación de aplicar */}
      <AlertDialog
        open={!!applyingFor}
        onOpenChange={(v) => !v && setApplyingFor(null)}
      >
        <AlertDialogContent data-testid="import-apply-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Aplicar importación · {applyingFor?.label}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Vas a escribir en la BD de SOFI 2.0 los datos importados
                  desde <b>{applyingFor?.source_url}</b>.
                </p>
                <p className="text-muted-foreground text-xs">
                  Idempotente: si ya se importó antes, los documentos existentes
                  se actualizarán por <code>external_id</code> — no se duplica.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => applyMut.mutate(applyingFor.tool_slug)}
              disabled={applyMut.isPending}
              data-testid="import-apply-confirm"
            >
              {applyMut.isPending ? "Aplicando…" : "Aplicar migración"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function ImporterCard({ item, loading, onPreview, onApply }) {
  const configured = item.configured;
  const last = item.last_run;
  const lastStatus = last?.status;
  // Heurística: si la URL del legacy apunta al mismo host que el propio SOFI,
  // es un env var mal configurado en producción. Muy común: al setup se puso
  // por error la URL de SOFI en lugar de la del legacy → todas las llamadas
  // dan 404. Bloqueamos el botón y mostramos aviso.
  const misconfiguredHost = React.useMemo(() => {
    if (!item.source_url) return false;
    try {
      const src = new URL(item.source_url).host.toLowerCase();
      const backend = new URL(process.env.REACT_APP_BACKEND_URL || window.location.origin).host.toLowerCase();
      const front = window.location.host.toLowerCase();
      return src === backend || src === front;
    } catch { return false; }
  }, [item.source_url]);
  const canRun = configured && !misconfiguredHost;

  return (
    <Card data-testid={`importer-card-${item.tool_slug}`}>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1 space-y-1.5">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{item.label}</h3>
            {misconfiguredHost ? (
              <Badge className="bg-amber-100 text-amber-800" data-testid={`importer-badge-misconfigured-${item.tool_slug}`}>
                <AlertTriangle className="mr-1 h-3 w-3" />Env var mal configurada
              </Badge>
            ) : configured ? (
              <Badge className="bg-emerald-100 text-emerald-800">
                <CheckCircle2 className="mr-1 h-3 w-3" />Configurado
              </Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-800">
                <AlertTriangle className="mr-1 h-3 w-3" />Sin configurar
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Env vars: <code>{item.source_base_url_env}</code> · <code>{item.source_token_env}</code>
          </div>
          {item.source_url && (
            <div className="text-xs text-muted-foreground">
              URL: <code className={misconfiguredHost ? "text-amber-700 font-medium" : ""}>{item.source_url}</code>
            </div>
          )}
          {misconfiguredHost && (
            <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900" data-testid={`importer-warning-${item.tool_slug}`}>
              <b>⚠️ Esta URL apunta a SOFI mismo, no al legacy.</b> Configura
              {" "}<code>{item.source_base_url_env}</code>{" "}con el dominio del legacy
              (ej. <code>https://pipeline.espiritutravel.com</code>) en el panel
              de deploy de Emergent y redeploy.
            </div>
          )}
          {last && (
            <div className="mt-2 rounded border bg-slate-50 p-2 text-[11px]">
              <div className="flex items-center gap-1.5">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <b>Último run:</b> {fmtDate(last.started_at)} ·
                {last.dry_run ? " dry-run" : " apply"} ·
                {lastStatus === "ok" ? (
                  <Badge className="ml-1 bg-emerald-100 text-emerald-800">OK</Badge>
                ) : (
                  <Badge className="ml-1 bg-red-100 text-red-800">Error</Badge>
                )}
                {last.elapsed_ms != null && <span className="ml-1">· {last.elapsed_ms} ms</span>}
              </div>
              {last.error && (
                <div className="mt-1 text-red-700">{last.error}</div>
              )}
              {last.stats && (
                <pre className="mt-1 whitespace-pre-wrap font-mono text-[10px] text-muted-foreground">
                  {JSON.stringify(last.stats, null, 2).slice(0, 500)}
                </pre>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:min-w-[180px]">
          <Button
            variant="outline"
            size="sm"
            onClick={onPreview}
            disabled={!canRun || loading}
            data-testid={`importer-preview-${item.tool_slug}`}
          >
            {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Eye className="mr-1.5 h-3.5 w-3.5" />}
            Previsualizar
          </Button>
          <Button
            size="sm"
            onClick={onApply}
            disabled={!canRun || loading}
            data-testid={`importer-apply-${item.tool_slug}`}
          >
            <Play className="mr-1.5 h-3.5 w-3.5" />
            Aplicar migración
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}


/**
 * Sección de "Correcciones de datos ad-hoc" — cada fix es un botón que ejecuta
 * un update en la BD (idempotente). El backend expone el catálogo con conteo
 * de docs pendientes de arreglar; cuando `pending === 0` el botón queda gris
 * porque ya no hay nada que corregir.
 */
function DataFixesSection() {
  const qc = useQueryClient();
  const [runningSlug, setRunningSlug] = useState(null);
  const [resultFor, setResultFor] = useState(null);

  const fixesQ = useQuery({
    queryKey: ["admin-data-fixes"],
    queryFn: async () => (await api.get("/admin/data-fixes")).data,
    refetchInterval: 60_000,
  });

  const runMut = useMutation({
    mutationFn: async (slug) => {
      setRunningSlug(slug);
      const res = await api.post(`/admin/data-fixes/${slug}/run`);
      return { slug, result: res.data };
    },
    onSuccess: ({ slug, result }) => {
      setResultFor({ slug, result });
      toast.success(`Fix "${slug}" ejecutado · ${result.total_modified} docs corregidos`);
      qc.invalidateQueries({ queryKey: ["admin-data-fixes"] });
    },
    onError: (e) => toast.error("Error: " + (e?.response?.data?.detail || e.message)),
    onSettled: () => setRunningSlug(null),
  });

  const items = fixesQ.data?.items || [];
  if (!items.length) return null;

  return (
    <div className="mt-8 space-y-3" data-testid="data-fixes-section">
      <div className="flex items-center gap-2">
        <Wrench className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Correcciones de datos</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Arreglos puntuales que actualizan la BD con un click. Todos son idempotentes:
        ejecutarlos varias veces no rompe nada, y cuando el contador de pendientes
        llega a 0, no hay nada más que hacer.
      </p>
      <div className="grid gap-3">
        {items.map((fix) => (
          <Card key={fix.slug} data-testid={`data-fix-card-${fix.slug}`}>
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{fix.label}</h3>
                  {fix.pending > 0 ? (
                    <Badge className="bg-amber-100 text-amber-800" data-testid={`data-fix-pending-${fix.slug}`}>
                      <AlertTriangle className="mr-1 h-3 w-3" />{fix.pending} pendientes
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-800" data-testid={`data-fix-clean-${fix.slug}`}>
                      <CheckCircle2 className="mr-1 h-3 w-3" />Ya limpio
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{fix.description}</p>
                {fix.last_run && (
                  <p className="text-[11px] text-muted-foreground">
                    <Clock className="mr-1 inline h-3 w-3" />
                    Última ejecución: {fmtDate(fix.last_run.ran_at)} · por {fix.last_run.user_email} · {fix.last_run.result?.total_modified || 0} docs modificados
                  </p>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => runMut.mutate(fix.slug)}
                disabled={fix.pending === 0 || runningSlug === fix.slug}
                data-testid={`data-fix-run-${fix.slug}`}
              >
                {runningSlug === fix.slug ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wrench className="mr-1.5 h-3.5 w-3.5" />}
                {fix.pending === 0 ? "Sin cambios" : `Ejecutar (${fix.pending})`}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Diálogo con detalle del resultado */}
      <AlertDialog open={!!resultFor} onOpenChange={(v) => !v && setResultFor(null)}>
        <AlertDialogContent className="max-w-lg" data-testid="data-fix-result-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fix ejecutado · {resultFor?.slug}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p><b>Total docs modificados:</b> {resultFor?.result?.total_modified}</p>
                <div className="rounded border bg-slate-50 p-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-muted-foreground"><th className="py-1">Operación</th><th className="py-1 text-right">Docs</th></tr>
                    </thead>
                    <tbody>
                      {(resultFor?.result?.steps || []).map((s, i) => (
                        <tr key={i} className="border-t"><td className="py-1 font-mono">{s.op}</td><td className="py-1 text-right tabular-nums">{s.modified}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cerrar</AlertDialogCancel></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
